import React from "react";

import { isRenewal } from "EmployeeApplication/urls";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { reduxForm, FieldArray, propTypes } from "redux-form";
import focusFirstError from "Utils/focusFirstError";
import { formatNameFL, formatNameFML } from "Utils/formatName";
import { isPartner } from "Utils/isPartner";
import { validateRequired, validateSigned, validateSSN } from "Utils/Validations";
import BasicLoading from "ZeroRuby/BasicLoading";

import { BodySmall } from "Cobalt/BodyText";
import { ErrorHandler } from "Cobalt/ErrorHandler";
import { SurfaceHeader, SurfaceSection } from "Cobalt/Surface";
import AgentHeader from "Common/Agent/AgentHeader";
import { SIFormField } from "Common/forms";
import { ssnMask } from "Common/forms/masks";
import { formFormatDate } from "Common/forms/utils";
import { Col, Row } from "Common/Grid";
import { usePartnerContext } from "Common/PartnerContext";
import PartnerSyncedPropertiesSection from "Common/PartnerSyncedPropertiesSection";
import SignatureForm from "Common/Signature";

import { stepCompleted } from "./actions";
import { useGetApplicationQuery, getEmployeeForm, getEmployeeApplication, getEnrollingDependents, useUpdateSignatureMutation } from "./api";
import ReviewCostsTable from "./components/ReviewCostsTable";
import FooterNavigation from "./FooterNavigation";
import Page from "./Page";
import { formattedAddress, capitalize, getFormatedEmployeeType, formatHiddenSSID } from "./selectors";

import type { EmployeeForm, EmployeeDependent } from "Utils/types";

const SYNCED_FIELDS_LABEL_MAP = {
  fullName: "Full Name",
  bornAt: "Date of Birth",
  hiredAt: "Date of Hire",
  physicalAddress: "Physical Addresss",
  employeeType: "Employee Type",
  socialSecurityNumber: "Social Security Number",
  homePhoneNumber: "Home Phone Number",
  workPhoneNumber: "Work Phone Number",
  jobTitle: "Job Title",
  gender: "Gender",
  maritalStatus: "Marital Status",
  email: "Email",
};

const { fieldArrayPropTypes } = propTypes;

type DumbProps = {
  backLink: string;
  basicInfoLink: string;
  onClickBack: () => void;
};

type SSSectionProps = {
  readonly dependents: Array<EmployeeDependent>;
  readonly employee: EmployeeForm;
  showEmployeeSSNField: boolean;
};

type DependentSSNsProps = {
  readonly dependents: Array<EmployeeDependent>;
  readonly fields: fieldArrayPropTypes;
};

const formatEmployeeData = (employee) => ({
  fullName: formatNameFML(employee),
  bornAt: employee.bornAt && formFormatDate(new Date(employee.bornAt)),
  hiredAt: employee.hiredAt && formFormatDate(new Date(employee.hiredAt)),
  physicalAddress: formattedAddress(employee),
  employeeType: getFormatedEmployeeType(employee),
  socialSecurityNumber: formatHiddenSSID(employee),
  homePhoneNumber: employee.homePhoneNumber,
  workPhoneNumber: employee.workPhoneNumber,
  jobTitle: employee.jobTitle,
  gender: employee.gender && capitalize(employee.gender),
  maritalStatus: employee.maritalStatus && capitalize(employee.maritalStatus),
  email: employee.email,
});

const getEditEmployeeLink = () => {
  const partnerContext = usePartnerContext();
  const filledByCompanyAdmin = window.location.pathname.includes("company-admin");

  return (
    <>
      {isPartner() &&
        (filledByCompanyAdmin && partnerContext.editEmployeeLink ? (
          <>
            <b>Note:</b> If any of the information to the left is incorrect,{" "}
            <a href={partnerContext.editEmployeeLink} target="_blank" rel="noreferrer">
              click here
            </a>{" "}
            to edit on your {partnerContext.partnerName} account.
          </>
        ) : (
          <>
            <b>Note:</b> If any of the information to the left is incorrect, please contact your company administrator to change the information on{" "}
            {partnerContext.partnerName}.
          </>
        ))}
    </>
  );
};

export const DependentSSNs = ({ dependents, fields }: DependentSSNsProps): React.ReactElement<any> => (
  <div>
    {fields.map((member, index) => (
      <Row key={index}>
        <SIFormField
          name={`${member}.socialSecurityNumber`}
          label={[formatNameFL(dependents[index])].join(" ")}
          hint="SSN or Tax ID"
          props={{ mask: ssnMask }}
          validate={[validateRequired, validateSSN]}
          md={4}
        />
      </Row>
    ))}
  </div>
);

const SSSection = ({ employee, dependents, showEmployeeSSNField }: SSSectionProps): React.ReactElement<any> => (
  <SurfaceSection title="Identity Confirmation">
    We need the social security number (or tax ID number) for each person in your family who's enrolling.
    {showEmployeeSSNField && (
      <Row>
        <SIFormField
          name="employeeForm.socialSecurityNumber"
          hint="SSN or Tax ID"
          label={formatNameFL(employee)}
          props={{ mask: ssnMask }}
          validate={[validateRequired, validateSSN]}
          md={4}
        />
      </Row>
    )}
    <FieldArray name="employeeDependents" component={DependentSSNs} dependents={dependents} />
  </SurfaceSection>
);

const formatPerson = ({ id, socialSecurityNumber }) => ({
  id,
  socialSecurityNumber,
});

const formatEmployeePolicy = ({ id, signatureImageDataUrl }) => ({
  id,
  signatureImageDataUrl,
});

const withReduxForm = reduxForm({
  form: "signApplicationForm",
  enableReinitialize: true,
  onSubmit: (values, _dispatch, props) => props.updateSignatureMutation(values, isRenewal()).unwrap(),
  onSubmitFail: (errors) => focusFirstError(errors, "."),
  onSubmitSuccess: (_, dispatch) => dispatch(stepCompleted("sign")),
});

const SignaturePageForm = withReduxForm(({ employeeForm, dependents, backLink, onClickBack, basicInfoLink, handleSubmit, error, isError }) => {
  const { syncedEmployeeFields } = usePartnerContext();
  const syncedEmployeeFieldNames = Object.keys(syncedEmployeeFields);
  // For partners that send us employee data (synced fields), we display two tables - one
  // with the synced data, and one with the other data that they filled out in the employee
  // application. The list of non-synced fields corresponds to the list of (nearly) all fields
  // minus synced fields, excluding special fields like employeeType and socialSecurityNumber.
  const nonSyncedEmployeeFieldNames = Object.keys(SYNCED_FIELDS_LABEL_MAP).filter(
    (fieldName) => syncedEmployeeFieldNames.indexOf(fieldName) === -1 && fieldName !== "employeeType" && fieldName !== "socialSecurityNumber",
  );

  const showReviewTables = syncedEmployeeFieldNames.length > 0;

  const employeeData = formatEmployeeData(employeeForm);
  const showEmployeeSSNField = !syncedEmployeeFields.socialSecurityNumber || !employeeData.socialSecurityNumber;

  return (
    <>
      <SurfaceHeader>Review and Sign</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>
          Please review your information and choices below, enter identity confirmation information, and sign to complete your application.
        </AgentHeader>
      </SurfaceSection>
      <form onSubmit={handleSubmit}>
        {showReviewTables && (
          <>
            <PartnerSyncedPropertiesSection
              syncedProperties={syncedEmployeeFieldNames.map((key) => [SYNCED_FIELDS_LABEL_MAP[key], employeeData[key]])}
              tableHeader="Imported Information"
              SyncActionComponent={getEditEmployeeLink()}
            />
            <PartnerSyncedPropertiesSection
              syncedProperties={nonSyncedEmployeeFieldNames.map((key) => [SYNCED_FIELDS_LABEL_MAP[key], employeeData[key]])}
              tableHeader="Personal Information"
              SyncActionComponent={
                <>
                  <b>Note:</b> If any of the information to the left is incorrect, <Link to={basicInfoLink}>click here</Link> to re-enter your information.
                </>
              }
            />
          </>
        )}

        <SurfaceSection title="Review Costs">
          <Row>
            <Col md={4}>
              <ReviewCostsTable />
            </Col>
          </Row>
        </SurfaceSection>
        {(showEmployeeSSNField || dependents.length > 0) && (
          <SSSection employee={employeeForm} dependents={dependents} showEmployeeSSNField={showEmployeeSSNField} />
        )}

        <SurfaceSection title="Signature">
          <BodySmall>By signing below, I agree that all the information I have provided is true and correct.</BodySmall>
          <Row>
            <SIFormField
              name="employeePolicy.signatureImageDataUrl"
              label="Signature"
              component={SignatureForm}
              props={{
                showSubmitButton: false,
                clearButtonText: "Clear Signature",
              }}
              validate={[validateRequired, validateSigned]}
              md={4}
            />
          </Row>
          <ErrorHandler name="SignatureField" action="validate" isError={isError} error={error} className="m-4 mt-0 w-full" />
        </SurfaceSection>
        <FooterNavigation backLink={backLink} onClickBack={onClickBack} />
      </form>
    </>
  );
});

export const SignaturePage = ({ backLink, onClickBack, basicInfoLink }: DumbProps) => {
  const { isLoading } = useGetApplicationQuery();
  const [updateSignatureMutation, { isError, error }] = useUpdateSignatureMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const enrolledDependents = useSelector(getEnrollingDependents);

  const initialValues = {
    employeeForm: formatPerson(employeeForm || {}),
    employeePolicy: formatEmployeePolicy(employeePolicy || {}),
    employeeDependents: (enrolledDependents || []).map(formatPerson),
  };

  return (
    <Page title="Review and Sign">
      {isLoading ? (
        <BasicLoading />
      ) : (
        <SignaturePageForm
          employeeForm={employeeForm}
          dependents={enrolledDependents}
          backLink={backLink}
          onClickBack={onClickBack}
          basicInfoLink={basicInfoLink}
          initialValues={initialValues}
          updateSignatureMutation={updateSignatureMutation}
          isError={isError}
          error={error}
        />
      )}
    </Page>
  );
};

export default SignaturePage;
